import React, {useEffect} from "react";
import {getKickVod2} from "@/components/DownloaderApp/kick";

const PREVIEW_TILE_WIDTH = 180;
const PREVIEW_TILE_HEIGHT = 101;
const PREVIEW_SHEET_COLUMNS = 5;
const MAX_PREVIEW_TILES = PREVIEW_SHEET_COLUMNS * 50;
const PREVIEW_DISPLAY_DURATION_MS = 3000;
const PREVIEW_FADE_DURATION_MS = 300;
const PREVIEW_CYCLE_DURATION_MS = PREVIEW_DISPLAY_DURATION_MS + PREVIEW_FADE_DURATION_MS;

function formatDuration(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
}

interface UpsellVodPreviewProps {
    variant?: 'original' | 'conversion';
    vodId: string | null;
    vodUrl: string;
    width?: number;
    className?: string;
    showPlaceholder?: boolean;
}

export function UpsellVodPreview({
                                     variant = 'conversion',
                                     vodId,
                                     vodUrl,
                                     width = 216,
                                     className = '',
                                     showPlaceholder = false,
                                 }: UpsellVodPreviewProps) {
    const [thumbnailSheetUrl, setThumbnailSheetUrl] = React.useState<string | null>(null);
    const [videoTitle, setVideoTitle] = React.useState<string | null>(null);
    const [videoDuration, setVideoDuration] = React.useState<number | null>(null);
    const [videoThumbnail, setVideoThumbnail] = React.useState<string | null>(null);
    const [previewIndex, setPreviewIndex] = React.useState(0);
    const [initialPreviewElapsed, setInitialPreviewElapsed] = React.useState(false);
    const [previewAnimationCycle, setPreviewAnimationCycle] = React.useState(0);
    const [tileFade, setTileFade] = React.useState(true);
    const previewAnimationStartedAt = React.useRef(Date.now());
    const videoThumbnailRef = React.useRef(videoThumbnail);
    videoThumbnailRef.current = videoThumbnail;

    const previewPositions = React.useMemo(() => {
        if (!videoDuration || videoDuration <= 0) return [0];
        const totalTiles = Math.min(Math.max(Math.ceil(videoDuration / 60), 1), MAX_PREVIEW_TILES);
        const percentages = [0, 0.2, 0.4, 0.6, 0.8];
        return percentages
            .map(percentage => Math.floor(percentage * (totalTiles - 1)))
            .filter((position, index, positions) => positions.indexOf(position) === index);
    }, [videoDuration]);

    useEffect(() => {
        if (!videoThumbnail) return;
        previewAnimationStartedAt.current = Date.now();
        setInitialPreviewElapsed(false);
        const initialPreviewTimeout = setTimeout(
            () => setInitialPreviewElapsed(true),
            PREVIEW_DISPLAY_DURATION_MS,
        );
        return () => clearTimeout(initialPreviewTimeout);
    }, [previewAnimationCycle, videoThumbnail, vodId]);

    useEffect(() => {
        if (!videoThumbnail || !thumbnailSheetUrl || !initialPreviewElapsed) return;
        setTileFade(false);
        const fadeTimeout = setTimeout(() => {
            setVideoThumbnail(null);
            setTileFade(true);
        }, PREVIEW_FADE_DURATION_MS);
        return () => clearTimeout(fadeTimeout);
    }, [initialPreviewElapsed, thumbnailSheetUrl, videoThumbnail]);

    useEffect(() => {
        if (!thumbnailSheetUrl || videoThumbnail || previewPositions.length <= 1) return;
        let previewTimeout: ReturnType<typeof setTimeout> | undefined;
        let fadeTimeout: ReturnType<typeof setTimeout> | undefined;

        const scheduleNextPreview = () => {
            previewTimeout = setTimeout(() => {
                setTileFade(false);
                fadeTimeout = setTimeout(() => {
                    setPreviewIndex(prev => (prev + 1) % previewPositions.length);
                    setTileFade(true);
                    scheduleNextPreview();
                }, PREVIEW_FADE_DURATION_MS);
            }, PREVIEW_DISPLAY_DURATION_MS);
        };

        scheduleNextPreview();
        return () => {
            if (previewTimeout) clearTimeout(previewTimeout);
            if (fadeTimeout) clearTimeout(fadeTimeout);
        };
    }, [thumbnailSheetUrl, previewPositions, videoThumbnail]);

    useEffect(() => {
        if (!vodId) return;
        const controller = new AbortController();
        let thumbnailObjectUrl: string | undefined;
        let lateSheetFadeTimeout: ReturnType<typeof setTimeout> | undefined;

        setThumbnailSheetUrl(null);
        setVideoThumbnail(null);
        setPreviewIndex(0);
        setInitialPreviewElapsed(false);
        setPreviewAnimationCycle(cycle => cycle + 1);
        setTileFade(true);
        previewAnimationStartedAt.current = Date.now();

        fetch(`/api/get-kick-thumbnail-sheet?url=${encodeURIComponent(vodUrl) || ''}&index=0`, {
            signal: controller.signal,
        })
            .then(res => {
                if (!res.ok) throw new Error('not ok');
                return res.blob();
            })
            .then(blob => {
                thumbnailObjectUrl = URL.createObjectURL(blob);
                const loadedAfterInitialPreview =
                    Date.now() - previewAnimationStartedAt.current >= PREVIEW_DISPLAY_DURATION_MS;

                if (loadedAfterInitialPreview && videoThumbnailRef.current) {
                    setInitialPreviewElapsed(false);
                    setTileFade(false);
                    setThumbnailSheetUrl(thumbnailObjectUrl);
                    lateSheetFadeTimeout = setTimeout(() => {
                        setVideoThumbnail(null);
                        setTileFade(true);
                        setPreviewAnimationCycle(cycle => cycle + 1);
                    }, PREVIEW_FADE_DURATION_MS);
                    return;
                }

                if (loadedAfterInitialPreview) {
                    setPreviewAnimationCycle(cycle => cycle + 1);
                }
                setThumbnailSheetUrl(thumbnailObjectUrl);
            })
            .catch(() => {});

        getKickVod2(vodUrl, null).then(result => {
            const video = result.json?.video2;
            if (video?.title) setVideoTitle(video.title);
            if (video?.duration != null && video.duration > 0) setVideoDuration(video.duration);
            const resolvedVideoThumbnail = video?.thumbnailUrl;
            if (resolvedVideoThumbnail) setVideoThumbnail(resolvedVideoThumbnail);
        }).catch(error => {
            console.error('[UpsellVodPreview] Failed to load Kick VOD details', error);
        });

        return () => {
            if (lateSheetFadeTimeout) clearTimeout(lateSheetFadeTimeout);
            controller.abort();
            if (thumbnailObjectUrl) URL.revokeObjectURL(thumbnailObjectUrl);
        };
    }, [vodId, vodUrl]);

    if (!vodId) return null;

    const previewWidth = width;
    const previewHeight = Math.round((width * PREVIEW_TILE_HEIGHT) / PREVIEW_TILE_WIDTH);
    const previewScale = width / PREVIEW_TILE_WIDTH;
    const containerClassName = `${variant === 'original' ? 'md:basis-1/3 ' : ''}flex flex-col items-center justify-center gap-2 ${className}`;
    const frameClassName = `relative overflow-hidden border border-white/20 ${
        variant === 'original' ? 'rounded-lg' : 'rounded-xl shadow-lg'
    }`;

    if (!videoThumbnail && !thumbnailSheetUrl) {
        if (!showPlaceholder) return null;
        return (
            <div className={containerClassName}>
                <div
                    className="animate-pulse rounded-xl border border-white/10 bg-white/5"
                    style={{width: previewWidth, height: previewHeight}}
                />
                <span className="h-[16px]" style={{width: previewWidth}}/>
            </div>
        );
    }

    const position = previewPositions[previewIndex];
    const col = position % PREVIEW_SHEET_COLUMNS;
    const row = Math.floor(position / PREVIEW_SHEET_COLUMNS);
    const isLoaderVisible = Boolean(
        thumbnailSheetUrl && (videoThumbnail || previewPositions.length > 1)
    );

    return (
        <div className={containerClassName}>
            <div
                className={frameClassName}
                style={{width: previewWidth, height: previewHeight}}
            >
                <img
                    src={videoThumbnail || thumbnailSheetUrl}
                    style={videoThumbnail ? {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: tileFade ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out',
                    } : {
                        width: PREVIEW_TILE_WIDTH,
                        height: PREVIEW_TILE_HEIGHT,
                        objectFit: 'none',
                        objectPosition: `${-col * PREVIEW_TILE_WIDTH}px ${-row * PREVIEW_TILE_HEIGHT}px`,
                        transformOrigin: 'top left',
                        transform: `scale(${previewScale})`,
                        opacity: tileFade ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out',
                    }}
                    alt="VOD preview"
                />
                <span className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                    Preview
                </span>
                <svg
                    key={previewAnimationCycle}
                    className="absolute top-1.5 right-1.5 size-6 rounded-full bg-black/70 p-1"
                    style={{visibility: isLoaderVisible ? 'visible' : 'hidden'}}
                    viewBox="0 0 20 20"
                    role="status"
                    aria-label="Loading next VOD preview"
                    aria-hidden={!isLoaderVisible}
                >
                    <circle
                        cx="10"
                        cy="10"
                        r="8"
                        fill="none"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="2"
                    />
                    <circle
                        cx="10"
                        cy="10"
                        r="8"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray="50.27"
                        transform="rotate(-90 10 10)"
                    >
                        <animate
                            attributeName="stroke-dashoffset"
                            from="0"
                            to="50.27"
                            dur={`${PREVIEW_CYCLE_DURATION_MS / 1000}s`}
                            repeatCount="indefinite"
                        />
                    </circle>
                </svg>
                {videoDuration > 0 && (
                    <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                        {formatDuration(videoDuration)}
                    </span>
                )}
            </div>
            <span
                className="h-[16px] truncate text-left text-xs text-neutral-300"
                style={{width: previewWidth}}
            >
                {videoTitle || ''}
            </span>
        </div>
    );
}
